import { Component } from '@theme/component';

/**
 * Announcement banner custom element that fades between messages.
 * Aurora Diva: autoplay keeps running (no pause on hover) and restarts
 * reliably after the tab is hidden and shown again.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  next() {
    this.current += 1;
    this.#restart();
  }

  previous() {
    this.current -= 1;
    this.#restart();
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.suspend();
    this.paused = false;
    this.#interval = setInterval(() => {
      if (document.hidden) return;
      this.current += 1;
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay) return;
    this.play();
  }

  /** After a manual arrow click, wait a full interval before the next change. */
  #restart() {
    if (this.autoplay && !this.paused) this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = parseInt(`${this.getAttribute('autoplay')}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;
    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
